import cx from 'clsx';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
dayjs.extend(LocalizedFormat);

import Badge from '@placekit/uikit/components/Badge';
import Icon from '@placekit/uikit/components/Icon';
import Link from '@placekit/uikit/components/Link';

import Section from 'components/Section';
import { applyDateOverride } from 'utils/blogOverrides';
import { getAllArticles } from 'utils/devto';
import { breadcrumbs } from 'utils/jsonld';
import SocialIcon from '@placekit/uikit/components/SocialIcon';

const BlogHome = ({ articles }) => (
  <div className="divide-y divide-gray-200">
    <Section paddings="first">
      <Section.Header level={1} title="PlaceKit blog">
        <p className="text-xl text-gray-500">
          We happen to write articles from time to time, about our experience running a SaaS
          business, along with some more technical ones.
        </p>
      </Section.Header>
    </Section>
    <Section theme="light">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {articles.map((article) => {
          const info = [
            {
              icon: 'clock',
              label: 'Average reading time',
              value: `${article.reading_time_minutes}m`,
            },
            {
              icon: 'chat-bubble-oval-left',
              label: 'Number of comments',
              value: article.comments_count,
            },
            {
              icon: 'hand-thumb-up',
              label: 'Number of positive reactions',
              value: article.positive_reactions_count,
            },
          ];
          return (
            <Link
              key={article.slug}
              href={`/blog/articles/${article.slug}`}
              className="group focus:outline-hidden transition-transform hover:scale-105 focus:scale-105"
            >
              <article className="h-full flex flex-col rounded-lg bg-white shadow-xl shadow-gray-800/5 overflow-hidden">
                <div className="grow p-6 flex flex-col gap-4">
                  <div className="flex items-center gap-2">
                    <span
                      className={cx(
                        'inline-flex p-2 rounded-lg ring-0',
                        'bg-accent-50 group-hover:bg-accent-500 group-focus:bg-accent-500',
                        'text-accent-700 group-hover:text-white group-focus:text-white',
                        'transition-colors',
                      )}
                      aria-hidden="true"
                    >
                      {article.tags.includes('launch') ? (
                        <Icon name="megaphone" size="small" />
                      ) : article.tags.includes('react') ? (
                        <SocialIcon name="react" branded={false} size="small" />
                      ) : article.tags.includes('geocoding') ? (
                        <Icon name="map-pin" size="small" />
                      ) : (
                        <Icon name="document-text" size="small" />
                      )}
                    </span>
                    <p className="text-sm leading-none text-gray-500">
                      <time dateTime={article.published_at}>
                        {dayjs(article.published_at).format('ll')}
                      </time>
                      {' by '}
                      <strong className="font-normal">{article.user.name}</strong>
                    </p>
                  </div>
                  <h2 className="font-medium leading-tight text-lg group-hover:text-accent-600 transition-colors">
                    {article.title}
                  </h2>
                  <p className="grow text-sm text-gray-700">{article.description}</p>
                  <p className="flex items-center gap-2 flex-wrap">
                    {article.tags.split(/[\s,]+/).map((tag) => (
                      <Badge key={tag} label={`#${tag}`} kind="neutral" />
                    ))}
                  </p>
                </div>
                <aside className="flex items-center justify-center gap-6 px-2 py-3 bg-gray-50 border-t border-gray-200">
                  {info.map((item) => (
                    <span
                      key={item.label}
                      title={item.label}
                      className="inline-flex items-center gap-1.5"
                    >
                      <Icon
                        name={item.icon}
                        size="small"
                        className="relative -top-px text-gray-400"
                      />
                      <span className="font-mono font-medium text-gray-700 text-sm">
                        {item.value}
                      </span>
                    </span>
                  ))}
                </aside>
              </article>
            </Link>
          );
        })}
      </div>
    </Section>
  </div>
);

export default BlogHome;

export async function getStaticProps() {
  const articles = (await getAllArticles())
    .map(applyDateOverride)
    .sort((a, b) => dayjs(b.published_at).valueOf() - dayjs(a.published_at).valueOf());
  return {
    props: {
      meta: {
        title: 'Blog',
        description:
          'The PlaceKit blog gathers articles we write about our experience running a SaaS business, along with some more technical ones.',
        jsonLd: [
          breadcrumbs([
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
          ]),
        ],
      },
      articles,
    },
  };
}
